// Admin "view as" (2026-09-25). Mounted once inside the router, around the
// whole app. While an admin views as a teacher or student:
//   - every useAuth() below here sees THAT person as the signed-in user (name,
//     id, role), so the real teacher / student pages render as they would for
//     them with no page-level changes. The admin's own tokens stay on the user
//     object: the admin API needs them to authorize each read.
//   - "Log out" anywhere in their chrome leaves view mode instead.
//   - a slim bar sits on top of every page (ViewAsBanner).
//   - pages outside that person's dashboard show a short notice (ViewAsRouteGate).
// Opening any admin page, signing out, or a non-admin account ends view mode.
// A teacher can view as a student in one of their classes the same way
// (2026-10-27): opening any teacher dashboard page ends it, and the way back is
// "Back to my class".
import { useCallback, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext, useAuth } from '../Accounts/AuthContext';
import { backLabelOf, endViewAs, getViewAs, homeOf, isViewerHome, routeAllowed, useViewAs, VIEWERS, viewerOf } from './viewAsStore';
import { installFetchGuard } from './fetchGuard';
import ViewAsBanner from './ViewAsBanner';
import './ViewAs.css';

installFetchGuard();

const noop = () => {};
const nothing = async () => null;
// Directory names are often "Last, First" (roster imports): use the part after
// the comma, else the first word.
export const firstName = (name) => {
  const n = String(name || '').trim();
  const given = n.includes(',') ? n.slice(n.indexOf(',') + 1).trim() : n;
  return given.split(/\s+/)[0] || n.split(',')[0].trim() || 'their';
};

export default function ViewAsProvider({ children }) {
  const real = useAuth();
  const va = useViewAs();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // The signed-in account must be the one that started the view.
  const viewer = viewerOf(va);
  const isViewer = real?.user?.role === viewer;
  const onHome = isViewerHome(viewer, pathname);
  const active = !!va && isViewer && !onHome;

  useEffect(() => { if (va && (onHome || !isViewer)) endViewAs(); }, [va, onHome, isViewer]);

  // The bar's height, for the sticky rails and fixed corner chips (ViewAs.css).
  useEffect(() => {
    const root = document.documentElement;
    if (active) root.setAttribute('data-view-as', va.kind);
    else root.removeAttribute('data-view-as');
    return () => root.removeAttribute('data-view-as');
  }, [active, va?.kind]);

  const exit = useCallback(() => {
    const current = getViewAs();
    const back = current?.returnTo || VIEWERS[viewerOf(current)]?.home || '/admin-dashboard';
    endViewAs();
    navigate(back);
  }, [navigate]);

  const value = useMemo(() => {
    if (!active) return real;
    const role = va.kind === 'teacher' ? 'Teacher' : 'Student';
    return {
      ...real,
      user: {
        ...real.user,
        username: va.id,
        sub: va.id,
        Name: va.name,
        fullName: va.name,
        email: va.email || '',
        role,
        viewAs: va,
      },
      // The legacy profile row the student pages read (school decides premium
      // offers: school accounts never see them). Never the admin's own row.
      userData: [{ School: { S: va.school || '' }, Role: { S: role }, Name: { S: va.name } }],
      // Never write the viewed person into the admin's own session (localStorage
      // sessionToken) or profile row. The real refreshToken keeps its own closure.
      updateUser: noop,
      updateUserData: noop,
      fetchDataFromDynamoDB: nothing,
      fetchUserDataFromDynamo: nothing,
      logout: exit,
      viewAs: va,
      exitViewAs: exit,
    };
  }, [active, real, va, exit]);

  return (
    <AuthContext.Provider value={value}>
      {active && <ViewAsBanner viewAs={va} onExit={exit} backLabel={backLabelOf(va)} />}
      {children}
    </AuthContext.Provider>
  );
}

// Wraps the routes: a page that is not part of the viewed person's dashboard
// (account settings, the diagnostic, Bluebook...) shows a notice instead.
export function ViewAsRouteGate({ children }) {
  const { viewAs, exitViewAs } = useAuth() || {};
  const { pathname } = useLocation();
  if (!viewAs || routeAllowed(viewAs.kind, pathname)) return children;
  return (
    <div className="bb-viewas-gate">
      <div className="bb-viewas-gate-card">
        <h1>This page is not part of view mode</h1>
        <p>
          View mode shows {viewAs.name}’s {viewAs.kind === 'teacher' ? 'teacher dashboard' : 'dashboard and practice'}.
          Pages like account settings stay private to them.
        </p>
        <div className="bb-viewas-gate-actions">
          <Link className="bb-viewas-gate-primary" to={homeOf(viewAs.kind)}>Back to {firstName(viewAs.name)}’s dashboard</Link>
          <button type="button" className="bb-viewas-gate-secondary" onClick={exitViewAs}>{backLabelOf(viewAs)}</button>
        </div>
      </div>
    </div>
  );
}
